'use client';

import React, { useMemo } from 'react';
import { Box, Container } from '@mui/material';
import styled from '@emotion/styled';
import Image from 'next/image';
import { color, spacing, breakpoints } from '@/theme/tokens';

const Section = styled(Box)`
    position: relative;
    display: flex;
    overflow: hidden;
`;

const Content = styled(Container)`
    position: relative;
    z-index: 2;
`;

const GradientOverlay = styled(Box)`
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(180deg, ${color.black[40]} 0%, ${color.black[20]} 40%, ${color.black[10]} 100%);
    z-index: 1;
`;

const BackgroundImageWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== '$viewport',
})<{ $viewport: 'mobile' | 'tablet' | 'desktop' }>`
    position: absolute;
    inset: 0;
    z-index: 0;

    ${({ $viewport }) => {
        switch ($viewport) {
            case 'mobile':
                return `
                    display: block;
                    @media (min-width: ${breakpoints.sm}) {
                        display: none;
                    }
                `;
            case 'tablet':
                return `
                    display: none;
                    @media (min-width: ${breakpoints.sm}) and (max-width: ${breakpoints.md}) {
                        display: block;
                    }
                `;
            case 'desktop':
                return `
                    display: none;
                    @media (min-width: ${breakpoints.md}) {
                        display: block;
                    }
                `;
        }
    }}
`;

interface BackgroundImageProps {
    backgroundImageMobile: string | undefined;
    backgroundImageTablet: string | undefined;
    backgroundImage: string | undefined;
    priority: boolean;
}

// Each variant is shown only in its own viewport range (see BackgroundImageWrapper), so `sizes` mirrors those
// media queries: 100vw inside the range, 1px outside it (the hidden variants then pick the smallest srcset
// candidate instead of triggering Next's "fill + sizes=100vw but not full width" dev warning).
const smMaxPx = parseInt(breakpoints.sm, 10) - 1;
const SIZES = {
    mobile: `(max-width: ${smMaxPx}px) 100vw, 1px`,
    tablet: `(min-width: ${breakpoints.sm}) and (max-width: ${breakpoints.md}) 100vw, 1px`,
    desktop: `(min-width: ${breakpoints.md}) 100vw, 1px`,
} as const;

function BackgroundImage({ 
    backgroundImageMobile, 
    backgroundImageTablet, 
    backgroundImage, 
    priority }: BackgroundImageProps) {
    return <>
        {backgroundImageMobile && (
            <BackgroundImageWrapper $viewport="mobile">
                <Image
                    src={backgroundImageMobile}
                    alt=""
                    fill
                    priority={priority}
                    sizes={SIZES.mobile}
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                />
            </BackgroundImageWrapper>
        )}
        {backgroundImageTablet && (
            <BackgroundImageWrapper $viewport="tablet">
                <Image
                    src={backgroundImageTablet}
                    alt=""
                    fill
                    priority={priority}
                    sizes={SIZES.tablet}
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                />
            </BackgroundImageWrapper>
        )}
        {backgroundImage && (
            <BackgroundImageWrapper $viewport="desktop">
                <Image
                    src={backgroundImage}
                    alt=""
                    fill
                    priority={priority}
                    sizes={SIZES.desktop}
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                />
            </BackgroundImageWrapper>
        )}
    </>
}
interface SectionWrapperProps {
    children: React.ReactNode;
    backgroundColor?: string;
    contentAlignItems?: 'center' | 'flex-start' | 'flex-end' | 'stretch';
    contentJustifyContent?: 'center' | 'flex-start' | 'flex-end' | 'space-between' | 'space-around';
    showGradientOverlay?: boolean;
    minHeightMobile?: string;
    backgroundImage?: string;
    backgroundImageMobile?: string;
    backgroundImageTablet?: string;
    alignItems?: 'center' | 'flex-start' | 'flex-end' | 'stretch';
    priority?: boolean;
}

export default function SectionWrapper({
    children,
    backgroundColor = color.gray[20],
    contentAlignItems = 'center',
    contentJustifyContent = 'space-between',
    showGradientOverlay = false,
    minHeightMobile,
    backgroundImage,
    backgroundImageMobile,
    backgroundImageTablet,
    alignItems = 'center',
    priority = false,
}: SectionWrapperProps) {
    const hasBackgroundImage = backgroundImage || backgroundImageMobile || backgroundImageTablet;

    const sectionStyles = useMemo(() => ({
        backgroundColor,
        paddingTop: spacing[32],
        paddingBottom: spacing[32],
        alignItems,
        justifyContent: 'center',
        [`@media (min-width: ${breakpoints.sm}) and (max-width: ${breakpoints.md})`]: {
            paddingTop: spacing[48],
            paddingBottom: spacing[48],
        },
        [`@media (min-width: ${breakpoints.md})`]: {
            paddingTop: spacing[64],
            paddingBottom: spacing[64],
        },
        [`@media (max-width: ${breakpoints.sm})`]: {
            ...(minHeightMobile && { minHeight: minHeightMobile }),
        },
    }), [backgroundColor, alignItems, minHeightMobile]);

    const contentStyles = useMemo(() => ({
        alignItems: contentAlignItems,
        justifyContent: contentJustifyContent,
    }), [contentAlignItems, contentJustifyContent]);

    return (
        <Section sx={sectionStyles}>
            {hasBackgroundImage && <BackgroundImage
                backgroundImageMobile={backgroundImageMobile}
                backgroundImageTablet={backgroundImageTablet}
                backgroundImage={backgroundImage}
                priority={priority}
            />}
            {showGradientOverlay && <GradientOverlay />}
            <Content sx={contentStyles}>
                {children}
            </Content>
        </Section>
    );
}
